import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import api from '../api/client';
import { clearSession, setSessionTokens } from '../api/session';
import { useSessionGuard } from '../hooks/useSessionGuard';
import { isChildPopup } from '../utils/popup';

interface User {
  user_id: number;
  username: string;
  full_name: string;
  role: string;
  must_change_password: boolean;
  dispatch_access: boolean;
  mobile_access: boolean;
  admin_access: boolean;
  superadmin_access: boolean;
  require_2fa: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  // False until a persisted token has been validated against the backend on
  // startup. Guards protected routes so a stale/expired token can't render the
  // app shell before it is checked.
  authReady: boolean;
}

// Public routes that must render without a validated session (kiosk board,
// login screens). We never validate or bounce on these.
function isPublicPath(pathname: string): boolean {
  return (
    pathname === '/login' ||
    pathname === '/mdt' ||
    pathname.startsWith('/board')
  );
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  // If there's no token (or we're on a public page), auth is already resolved.
  // Otherwise stay "not ready" until we validate the token against the backend.
  const [authReady, setAuthReady] = useState<boolean>(
    () => !localStorage.getItem('token') || isPublicPath(window.location.pathname)
  );

  const login = useCallback(async (username: string, password: string) => {
    const res = await api.post('/auth/login', { username, password });
    const data = res.data;
    setSessionTokens('web', data.access_token, data.refresh_token);
    const userData: User = {
      user_id: data.user_id,
      username,
      full_name: data.full_name,
      role: data.role,
      must_change_password: data.must_change_password,
      dispatch_access: data.dispatch_access,
      mobile_access: data.mobile_access,
      admin_access: data.admin_access,
      superadmin_access: data.superadmin_access,
      require_2fa: data.require_2fa,
    };
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(data.access_token);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
    setUser(null);
  }, []);

  // Sliding session: renew silently while the user works, sign out after the
  // tenant's configured inactivity window.
  const isMdtSession = window.location.pathname.startsWith('/mobile/');
  const handleIdleExpire = useCallback(() => {
    clearSession();
    setToken(null);
    setUser(null);
    if (isChildPopup()) {
      window.close();
      return;
    }
    window.location.href = isMdtSession ? '/mdt?idle=1' : '/login?idle=1';
  }, [isMdtSession]);
  const { idleCountdown, keepAlive } = useSessionGuard(
    !!token && !isPublicPath(window.location.pathname),
    isMdtSession,
    handleIdleExpire
  );

  // On startup, validate a persisted token against the backend before letting
  // protected routes render. An expired/invalid token is cleared and the user
  // is sent to login instead of seeing the app shell with stale access.
  useEffect(() => {
    if (authReady) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/auth/me');
        if (cancelled) return;
        const d = res.data;
        const userData: User = {
          user_id: d.id,
          username: d.username,
          full_name: d.full_name,
          role: d.role,
          must_change_password: d.must_change_password,
          dispatch_access: d.dispatch_access,
          mobile_access: d.mobile_access,
          admin_access: d.admin_access,
          superadmin_access: d.superadmin_access,
          require_2fa: d.require_2fa,
        };
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      } catch {
        if (cancelled) return;
        // Invalid/expired token — clear it so ProtectedRoute redirects to login.
        // (The api client already tried a silent refresh first.)
        clearSession();
        setToken(null);
        setUser(null);
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authReady]);

  // Cross-tab logout: when the token is cleared in any other tab/pane,
  // clear auth here too and bounce to login so all panes log out together.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'token' && !e.newValue) {
        setToken(null);
        setUser(null);
        // Popup windows (Map/Units/Chat/etc.) close themselves on logout so only the main window remains.
        if (isChildPopup()) {
          window.close();
          return;
        }
        if (!window.location.pathname.includes('/login')) {
          window.location.href = window.location.pathname.includes('mobile/mdt') ? '/mdt' : '/login';
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!token, authReady }}>
      {children}
      <Dialog open={idleCountdown !== null} onClose={keepAlive}>
        <DialogTitle>Still there?</DialogTitle>
        <DialogContent>
          <Typography>
            You'll be signed out in {idleCountdown ?? 0} second{idleCountdown === 1 ? '' : 's'} due to
            inactivity.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={keepAlive} autoFocus>
            Stay signed in
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
